import React, { useMemo, useState } from "react";
import SearchField from "../SearchField";
import PersonSheetHost from "./PersonSheetHost";

function teamActivityHint(status) {
  if (status === "ACTIVE") return "Currently on the team";
  return "Invited or no longer on staff";
}

function studentActivityHint(status) {
  if (status === "ACTIVE") return "Has credits or an active membership";
  return "No credits left";
}

function formatCredits(balance) {
  const n = Number(balance) || 0;
  return `${n} credit${n === 1 ? "" : "s"}`;
}

function matchesPeopleQuery(name, query) {
  if (!query) return true;
  return String(name || "").toLowerCase().includes(query);
}

export default function SchoolPeoplePanel({
  schoolId,
  schoolName = "",
  team = [],
  people = [],
  onRefresh,
  compact,
}) {
  const [detailPerson, setDetailPerson] = useState(null);
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();

  const filteredPeople = useMemo(
    () => people.filter((p) => matchesPeopleQuery(p.name, query)),
    [people, query],
  );

  function openTeamMember(m) {
    if (!m.organizer_id) return;
    setDetailPerson({
      id: m.organizer_id,
      name: m.name,
      phone: m.phone,
      isPerson: false,
      membership: {
        id: m.id,
        role: m.role,
      },
      activity: m.activity,
      activity_label: m.activity_label,
      note: m.note,
    });
  }

  function openPerson(p) {
    setDetailPerson({
      ...p,
      isPerson: true,
    });
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-6"}>
      <section>
        <h3 className="font-ui font-semibold text-[15px] mb-1">Team</h3>
        <ul className="divide-y divide-black/[0.08]">
          {team.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                className={`w-full text-left flex items-center justify-between gap-2 py-3 text-sm min-h-[44px] ${
                  m.organizer_id ? "hover:bg-black/[0.03]" : "opacity-60 cursor-default"
                }`}
                onClick={() => openTeamMember(m)}
                disabled={!m.organizer_id}
              >
                <span className="min-w-0">
                  <span className="font-medium">{m.name}</span>
                  <span className="text-ink/60 ml-2">
                    {m.role}
                  </span>
                </span>
                {m.activity_label ? (
                  <span
                    className="text-[13px] text-ink/60 shrink-0"
                    title={teamActivityHint(m.activity)}
                  >
                    {m.activity_label}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
          {!team.length && <li className="py-3 text-sm text-ink/60">No co-admins or instructors yet.</li>}
        </ul>
      </section>

      <section>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="font-ui font-semibold text-[15px]">People</h3>
          {query && people.length > 0 ? (
            <p className="text-[12px] text-ink/60 shrink-0">
              {filteredPeople.length} of {people.length}
            </p>
          ) : null}
        </div>
        {people.length > 0 ? (
          <SearchField
            className="mb-1"
            value={search}
            onChange={setSearch}
            label="Search people by name"
            placeholder="Search by name"
            testId="school-people-search"
            clearTestId="school-people-search-clear"
            inputClassName="min-h-[44px]"
          />
        ) : null}
        <ul className="divide-y divide-black/[0.08]">
          {filteredPeople.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="w-full text-left flex items-center justify-between gap-2 py-3 text-sm min-h-[44px] hover:bg-black/[0.03]"
                onClick={() => openPerson(p)}
                data-testid={`school-person-${p.id}`}
              >
                <span className="min-w-0 truncate">
                  <span className="font-medium">{p.name || "Dancer"}</span>
                  <span className="sr-only">
                    {`, ${p.activity_label || "Inactive"}`}
                  </span>
                </span>
                <span
                  className="text-[13px] text-ink/60 shrink-0"
                  title={`${p.activity_label || "Inactive"} — ${studentActivityHint(p.activity)}`}
                >
                  {formatCredits(p.credit_balance)}
                </span>
              </button>
            </li>
          ))}
          {!people.length && (
            <li className="py-3 text-sm text-ink/60">No followers or students yet — share your invite link.</li>
          )}
          {people.length > 0 && filteredPeople.length === 0 && (
            <li className="py-3 text-sm text-ink/60">No one matches “{search.trim()}”.</li>
          )}
        </ul>
      </section>

      {detailPerson && (
        <PersonSheetHost
          schoolId={schoolId}
          schoolName={schoolName}
          person={detailPerson}
          onClose={() => setDetailPerson(null)}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}
